/**
 * Размерная линия по правилам чертежа: выносные линии от объекта,
 * размерная линия между ними, засечки под 45° и подпись длины.
 *
 * Отступ задаётся снаружи (виджет решает, с какой стороны стены рисовать),
 * а толщина линий и размер текста считаются в пикселях экрана — размеры
 * не должны утолщаться при приближении.
 */
import { add, angleOf, dist, formatLength, mul, norm, sub, type Pt } from "@/shared/lib/geometry";
import { useLengthUnit } from "@/shared/store";

const DEG = 180 / Math.PI;

interface Props {
	from: Pt;
	to: Pt;
	/** Единичная нормаль: в эту сторону выносится размер */
	normal: Pt;
	/** Расстояние от объекта до размерной линии, мм */
	distance: number;
	mmPerPx: number;
	/** Своя подпись вместо автоматической длины */
	label?: string;
	muted?: boolean;
	/** Минимальная длина, при которой размер показывается. */
	minLength?: number;
}

export const DimensionLine = ({
	from,
	to,
	normal,
	distance,
	mmPerPx,
	label,
	muted,
	minLength = 200,
}: Props) => {
	const unit = useLengthUnit();
	const length = dist(from, to);
	if (length < minLength) return null;

	const px = (n: number) => n * mmPerPx;
	const color = muted ? "#9aa5b6" : "#5b6678";

	const a = add(from, mul(normal, distance));
	const b = add(to, mul(normal, distance));
	const dir = norm(sub(b, a));
	const tick = mul(add(dir, normal), px(4)); // засечка под 45°

	let deg = angleOf(sub(b, a)) * DEG;
	let flip = false;
	if (deg > 90 || deg < -90) {
		deg += 180;
		flip = true;
	}
	const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
	const textShift = flip ? px(-6) : px(6);
	const textPos = {
		x: mid.x - normal.x * textShift,
		y: mid.y - normal.y * textShift,
	};

	return (
		<g style={{ pointerEvents: "none" }}>
			{/* Выносные линии */}
			<line
				x1={add(from, mul(normal, px(3))).x}
				y1={add(from, mul(normal, px(3))).y}
				x2={add(a, mul(normal, px(4))).x}
				y2={add(a, mul(normal, px(4))).y}
				stroke={color}
				strokeWidth={px(0.75)}
			/>
			<line
				x1={add(to, mul(normal, px(3))).x}
				y1={add(to, mul(normal, px(3))).y}
				x2={add(b, mul(normal, px(4))).x}
				y2={add(b, mul(normal, px(4))).y}
				stroke={color}
				strokeWidth={px(0.75)}
			/>

			{/* Размерная линия с засечками */}
			<line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={color} strokeWidth={px(0.9)} />
			<line
				x1={a.x - tick.x}
				y1={a.y - tick.y}
				x2={a.x + tick.x}
				y2={a.y + tick.y}
				stroke={color}
				strokeWidth={px(1.2)}
			/>
			<line
				x1={b.x - tick.x}
				y1={b.y - tick.y}
				x2={b.x + tick.x}
				y2={b.y + tick.y}
				stroke={color}
				strokeWidth={px(1.2)}
			/>

			<text
				x={textPos.x}
				y={textPos.y}
				transform={`rotate(${deg} ${textPos.x} ${textPos.y})`}
				textAnchor="middle"
				fontSize={11 * mmPerPx}
				fill={muted ? "#8b96a7" : "#39414f"}
				style={{ userSelect: "none" }}
			>
				{label ?? formatLength(length, unit)}
			</text>
		</g>
	);
};
